import React from 'react'
import { Checkbox, Col, Divider, Form, FormInstance, Input, InputNumber, Row, Select, Typography } from 'antd'

import IntlMessages from '../../util/IntlMessages'
import { filterOptions } from '../../util/filter-options'
import { filterCountryOptions } from '@vacationtracker/shared/functions/filter-countries'
import { ICountry, countriesForPicker, hasSubdivisions, subdivisionOptions, subdivisionTermKey } from '@vacationtracker/shared/data/countries'
import aboutUsSurvey from '@vacationtracker/shared/data/survey'

import { Platform } from '@vacationtracker/shared/types/core-event'
import { UserSignUpForm } from '../user-signup-form'
import SurveyFormPart from '../survey'

const { Option } = Select
const { Paragraph, Title } = Typography

interface IColSize {
  span: number
  offset: number
}

interface ICreateCompanyForm {
  onBlurContactEmail: () => void
  onCountryChange: (countryId: string) => void
  selectedCountry?: ICountry
  hasUnlimitedDays: boolean
  onChangeDaysPerYear: (days: number) => void
  onUnlimitedPTOChange: () => void
  validation: () => void
  onAgreeToTermsChange: () => void
  col?:
    | {
        xxl: IColSize
        xl: IColSize
        lg: IColSize
        md: IColSize
        xs: IColSize
      }
    | {
        span: number
        offset: number
      }
  userEmail?: string
  userPassword?: string
  userRepeatPassword?: string
  platform: Platform
  form: FormInstance
  showLeaveQuotas?: boolean
}

const CreateCompanyForm = ({
  onBlurContactEmail,
  onCountryChange,
  selectedCountry,
  hasUnlimitedDays,
  onChangeDaysPerYear,
  onUnlimitedPTOChange,
  validation,
  onAgreeToTermsChange,
  platform,
  form,
  showLeaveQuotas = true,
}: ICreateCompanyForm): React.ReactElement => {
  return (
    <>
      {platform === 'email' && (
        <>
          <Row
            gutter={{
              xs: 8,
              sm: 16,
              md: 24,
              lg: 32,
            }}
          >
            <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
              <Title level={4}>
                <IntlMessages id="app.user" />
              </Title>
              <Paragraph type="secondary">
                <IntlMessages id="createCompany.steps.companyForm.UserInfoSectionDescription" />
              </Paragraph>
            </Col>
            <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
              <UserSignUpForm addAvatar={false} form={form} />
            </Col>
          </Row>

          <Divider />
        </>
      )}

      <Row
        gutter={{
          xs: 8,
          sm: 16,
          md: 24,
          lg: 32,
        }}
      >
        <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
          <Title level={4}>
            <IntlMessages id="app.company" />
          </Title>
          <Paragraph type="secondary">
            <IntlMessages id="createCompany.steps.companyForm.companyInfoSectionDescription" />
          </Paragraph>
        </Col>
        <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
          <Form.Item
            className="wrap-label"
            label={<IntlMessages id="createCompany.steps.companyForm.email" />}
            extra={<IntlMessages id="createCompany.steps.companyForm.contactEmailExtra" />}
            name="contactEmail"
            rules={[
              {
                required: true,
                message: <IntlMessages id="createCompany.steps.companyForm.contactEmail.validation.required" />,
              },
              {
                type: 'email',
                message: <IntlMessages id="form.validEmailRequired" />,
              },
            ]}
          >
            <Input type="email" data-qa="company-contact-email" onBlur={onBlurContactEmail} className="data-hj-allow" />
          </Form.Item>
          <Form.Item
            className="wrap-label"
            label={<IntlMessages id="general.companyName" />}
            name="name"
            rules={[
              {
                required: true,
                message: <IntlMessages id="error.fieldIsRequired" />,
              },
            ]}
          >
            <Input className="data-hj-allow" data-qa="company-name-input" />
          </Form.Item>

          <Form.Item
            className="wrap-label"
            label={<IntlMessages id="app.country" />}
            name="country"
            rules={[{ required: true, message: <IntlMessages id="error.fieldIsRequired" /> }]}
          >
            <Select onChange={onCountryChange} filterOption={filterCountryOptions} className="data-hj-allow" showSearch>
              {countriesForPicker().map((country) => (
                <Option value={country.iso} key={country.iso}>
                  {country.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {selectedCountry && hasSubdivisions(selectedCountry.iso) && (
            <Form.Item
              className="wrap-label"
              label={<IntlMessages id={subdivisionTermKey(selectedCountry.iso)} />}
              name="state"
              rules={[{ required: true, message: <IntlMessages id="error.fieldIsRequired" /> }]}
            >
              <Select filterOption={filterOptions} className="data-hj-allow" showSearch>
                {subdivisionOptions(selectedCountry.iso).map((subdivision) => (
                  <Option value={subdivision.value} key={subdivision.value}>
                    {subdivision.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {showLeaveQuotas && (
            <div>
              <Title style={{ marginBottom: 15, marginTop: 15 }} level={5}>
                <IntlMessages id="createCompany.steps.companyForm.companyLeaveSection" />
              </Title>
              <Row>
                <Col span={12}>
                  <Form.Item
                    className="wrap-label"
                    labelCol={{ span: 16 }}
                    style={{ marginBottom: 5 }}
                    label={<IntlMessages id="createCompany.steps.companyForm.defaultPtoQuota" />}
                    name="daysPerYear"
                    rules={[
                      { required: !hasUnlimitedDays, message: <IntlMessages id="createCompany.steps.companyForm.defaultPtoQuota.validation" /> },
                      { type: 'number', min: 0, message: <IntlMessages id="createCompany.steps.companyForm.defaultPtoQuota.validation.minDefaultPtoQuota" /> },
                      { type: 'number', max: 366, message: <IntlMessages id="createCompany.steps.companyForm.defaultPtoQuota.validation.maxDefaultPtoQuota" /> },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      max={365}
                      style={{ width: 70 }}
                      onChange={(value) => onChangeDaysPerYear(value as number)}
                      disabled={hasUnlimitedDays}
                      className="data-hj-allow"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    className="wrap-label"
                    labelCol={{ span: 16 }}
                    style={{ marginBottom: 5 }}
                    label={<IntlMessages id="createCompany.steps.companyForm.unlimitedLeavePolicy" />}
                    name="hasUnlimitedDays"
                    valuePropName="checked"
                    labelAlign="right"
                  >
                    <Checkbox onChange={onUnlimitedPTOChange} />
                  </Form.Item>
                </Col>
              </Row>
              <p style={{ textAlign: 'center' }} className="ant-typography ant-typography-secondary">
                <IntlMessages id="createCompany.steps.companyForm.defaultPtoQuotaExtra" />
              </p>
            </div>
          )}
        </Col>
      </Row>

      <Divider />

      <Row
        gutter={{
          xs: 8,
          sm: 16,
          md: 24,
          lg: 32,
        }}
      >
        <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
          <Title level={4}>
            <IntlMessages id="surveyAboutUs.title" />
          </Title>
          <Paragraph type="secondary">
            <IntlMessages id="surveyAboutUs.description" />
          </Paragraph>
        </Col>
        <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
          <SurveyFormPart form={form} survey={aboutUsSurvey} />
        </Col>
      </Row>

      <Divider />

      <Row
        gutter={{
          xs: 8,
          sm: 16,
          md: 24,
          lg: 32,
        }}
      >
        <Col span={24}>
          <div style={{ textAlign: 'left', marginBottom: 30, marginTop: 30 }}>
            <Form.Item className="wrap-label" name="agreeToTerms" valuePropName="checked" rules={[{ validator: validation }]} wrapperCol={{ offset: 0, span: 24 }}>
              <Checkbox onChange={onAgreeToTermsChange}>
                <IntlMessages
                  id="createCompany.steps.companyForm.agreeTerms"
                  values={{
                    // eslint-disable-next-line
                    aTOS: (...chunks) => (
                      <a href="https://vacationtracker.io/terms-of-service/" target="_blank" rel="noopener noreferrer">
                        {chunks}
                      </a>
                    ),
                    // eslint-disable-next-line
                    aPP: (...chunks) => (
                      <a href="https://vacationtracker.io/privacy-policy/" target="_blank" rel="noopener noreferrer">
                        {chunks}
                      </a>
                    ),
                    // eslint-disable-next-line
                    aDPA: (...chunks) => (
                      <a href="https://vacationtracker.io/dpa/" target="_blank" rel="noopener noreferrer">
                        {chunks}
                      </a>
                    ),
                  }}
                />
              </Checkbox>
            </Form.Item>
          </div>
        </Col>
      </Row>
    </>
  )
}

export default CreateCompanyForm
